import { Routes } from '@angular/router';
import { DonationComponent } from 'features/src/lib/donations/components/donation/donation.component';
import { DonationDetailsComponent } from 'features/src/lib/donations/components/donation-details/donation-details.component';
import { RecurringDonationConfirmationComponent } from 'features/src/lib/donations/components/recurring-confirmation/recurring-donation-confirmation.component';
import { PopUPComponent } from 'features/src/lib/donations/components/pop-up/popup.component';
import { ThankYouStepComponent } from 'features/src/lib/donations/components/thanks-step/thank-you-step.component';
import {
  DONATION_ROUTE_CONFIGS,
  DonationRouteConfig,
  DonationRouteData,
} from 'features/src/lib/donations/types/donation-routes';

/**
 * Turn a `DonationRouteConfig` into a static tree of concrete Angular routes.
 * We deliberately avoid a single dynamic `:donationType/step/:step` route so
 * Angular can lazy-know every step's component and route data at bootstrap.
 */
function createDonationTypeRoutes(config: DonationRouteConfig): Routes {
  const stepRoutes: Routes = config.intermediateSteps.map(
    ({ step, componentTag }) => ({
      path: `step/${step}`,
      component:
        componentTag === 'details'
          ? DonationDetailsComponent
          : RecurringDonationConfirmationComponent,
      data: {
        donationType: config.donationType,
        step,
      } satisfies DonationRouteData,
    })
  );

  return [
    ...stepRoutes,
    {
      path: `step/${config.finalStep}`,
      component: ThankYouStepComponent,
      data: {
        donationType: config.donationType,
        step: config.finalStep,
      } satisfies DonationRouteData,
    },
    { path: '**', redirectTo: 'step/2' },
  ];
}

const buildDonationChildren = (): Routes => [
  { path: '', redirectTo: 'step/1', pathMatch: 'full' },
  {
    path: 'pop-up',
    component: PopUPComponent,
  },
  {
    path: 'step/:n',
    component: DonationComponent,
    // Step 1 doesn't belong to a specific donation type yet; the value is
    // decided when the donor submits the price form.
    data: { step: 1 },
  },
  ...DONATION_ROUTE_CONFIGS.map((config) => ({
    path: config.donationType,
    children: createDonationTypeRoutes(config),
  })),
  { path: '**', redirectTo: 'step/1' },
];

export const routes: Routes = [
  {
    path: 'contact-us',
    loadComponent: () =>
      import('./website/contact-us/contact-us/contact-us.component').then(
        (c) => c.ContactUsComponent
      ),
  },
  {
    path: 'crezco-story',
    loadComponent: () =>
      import('./website/crezco-story/crezco-story/crezco-story.component').then(
        (c) => c.CrezcoStoryComponent
      ),
  },
  {
    path: 'donation-modal',
    outlet: 'modal',
    loadComponent: () =>
      import('./website/stripe/donation-wrapper.component').then(
        (m) => m.DonationWrapperComponent
      ),
    children: buildDonationChildren(),
  },
  {
    path: 'donation-custom',
    loadComponent: () =>
      import('./website/stripe/donation-wrapper.component').then(
        (m) => m.DonationWrapperComponent
      ),
    children: buildDonationChildren(),
  },
  {
    path: 'projects/university',
    loadComponent: () =>
      import(
        './website/projects/university-sponsorship/university-sponsorship.component'
      ).then((c) => c.UniversitySponsorshipComponent),
  },
  {
    path: 'projects/special-education',
    loadComponent: () =>
      import(
        './website/projects/special-education/special-education.component'
      ).then((c) => c.SpecialEducationComponent),
  },
  {
    path: 'projects/community-support',
    loadComponent: () =>
      import(
        './website/projects/community-support/community-support.component'
      ).then((c) => c.CommunitySupportComponent),
  },
  {
    path: 'projects/mission-trip',
    loadComponent: () =>
      import(
        './website/projects/student-mission-trip/student-mission-trip.component'
      ).then((c) => c.StudentMissionTripComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('./website/home/home.component').then((c) => c.HomeComponent),
  },
];
